import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const OFFER_END = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

function useCountdown(target: Date) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target.getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return timeLeft;
}

function useVisible(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function AlarmPromoPage() {
  const countdown = useCountdown(OFFER_END);
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const benefitsAnim = useVisible();
  const packagesAnim = useVisible();
  const stepsAnim = useVisible();
  const testimonialsAnim = useVisible();
  const formAnim = useVisible();
  const urgencyAnim = useVisible();

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById('claim-offer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const fd = new FormData(form);
    const msg = fd.get('message') as string;
    if (msg && msg.length > 500) { setSubmitting(false); return; }
    const data = new URLSearchParams();
    data.append('first_name', fd.get('first_name') as string);
    data.append('last_name', fd.get('last_name') as string);
    data.append('email', fd.get('email') as string);
    data.append('phone', fd.get('phone') as string);
    data.append('property_type', fd.get('property_type') as string);
    data.append('city', fd.get('city') as string);
    data.append('message', msg || '');
    try {
      const res = await fetch('https://readdy.ai/api/form/d6joe1apaf6ievb71j50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setFormSuccess(true); form.reset(); }
    } catch (error) {
      console.error('Form submission error:', error);
    }
    setSubmitting(false);
  };

  const pad = (n: number) => String(n).padStart(2, '0');

  const packages = [
    {
      name: 'Essential',
      badge: 'Most Popular',
      badgeColor: 'bg-[#D4A853] text-white',
      originalPrice: '$799',
      promoPrice: '$399',
      monthly: '$29',
      highlight: false,
      features: [
        'Wireless Control Panel',
        '3 Door/Window Sensors',
        '1 Motion Detector',
        'Mobile App Control',
        '24/7 Professional Monitoring',
        'Battery Backup',
        'Free Professional Installation',
      ],
    },
    {
      name: 'Advanced',
      badge: 'Best Value',
      badgeColor: 'bg-[#0A1628] text-white',
      originalPrice: '$1,299',
      promoPrice: '$649',
      monthly: '$39',
      highlight: true,
      features: [
        'Smart Touchscreen Panel',
        '6 Door/Window Sensors',
        '2 Motion Detectors',
        '1 Indoor HD Camera',
        'Mobile App + Smart Home Integration',
        '24/7 Professional Monitoring',
        'Battery Backup + Cellular Backup',
        'Free Professional Installation',
        'Free 1-Year Service Plan',
      ],
    },
    {
      name: 'Premium',
      badge: 'Complete Protection',
      badgeColor: 'bg-[#D4A853]/20 text-[#D4A853]',
      originalPrice: '$1,999',
      promoPrice: '$999',
      monthly: '$49',
      highlight: false,
      features: [
        'Smart Touchscreen Panel',
        '10 Door/Window Sensors',
        '3 Motion Detectors',
        '2 Indoor + 1 Outdoor HD Camera',
        'Smart Lock Integration',
        'Mobile App + Alexa/Google Home',
        '24/7 Professional Monitoring',
        'Cellular + Battery Backup',
        'Free Professional Installation',
        'Free 2-Year Service Plan',
      ],
    },
  ];

  const steps = [
    { icon: 'ri-phone-line', title: 'Call or Submit', desc: 'Contact us today to claim your limited-time promo pricing before the offer expires.' },
    { icon: 'ri-search-eye-line', title: 'Free Assessment', desc: 'A certified technician visits your property and designs a custom security plan at no cost.' },
    { icon: 'ri-tools-line', title: 'Professional Install', desc: 'Our factory-certified team installs your system quickly — most jobs done in under 4 hours.' },
    { icon: 'ri-shield-check-line', title: 'Stay Protected', desc: 'Enjoy 24/7 monitoring, mobile app control, and lifetime support from day one.' },
  ];

  const testimonials = [
    { name: 'Karen M.', location: 'Mississauga, ON', stars: 5, text: 'We caught the promo just in time. The installation was seamless and the app is incredibly easy to use. Best investment we\'ve made for our home.' },
    { name: 'Tony R.', location: 'Toronto, ON', stars: 5, text: 'Half-price installation and the system is top-notch. The technician was professional and walked us through everything. Highly recommend!' },
    { name: 'Linda S.', location: 'Brampton, ON', stars: 5, text: 'I was skeptical about the promo but it\'s 100% legit. Got a full alarm system installed for a fraction of the regular price. Amazing service.' },
  ];

  const promoStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Alarm Promotion', item: 'https://alliancesecuritysystems.com/alarm-promo' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SaleEvent',
      name: '50% Off Alarm System Installation — Limited Time GTA Offer',
      description: 'Save 50% on professional alarm system installation for Greater Toronto Area homeowners. Includes free home security assessment, 24/7 professional monitoring, and factory-certified installation.',
      url: 'https://alliancesecuritysystems.com/alarm-promo',
      organizer: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      location: {
        '@type': 'AdministrativeArea',
        name: 'Greater Toronto Area',
      },
      offers: [
        {
          '@type': 'Offer',
          name: 'Essential Alarm Package — Promo Price',
          description: 'Wireless Control Panel, 3 Door/Window Sensors, 1 Motion Detector, Mobile App Control, 24/7 Professional Monitoring, Battery Backup, Free Professional Installation. Original price $799.',
          price: '399',
          priceCurrency: 'CAD',
          availability: 'https://schema.org/InStock',
          priceValidUntil: '2025-12-31',
          url: 'https://alliancesecuritysystems.com/alarm-promo',
        },
        {
          '@type': 'Offer',
          name: 'Advanced Alarm Package — Promo Price',
          description: 'Smart Touchscreen Panel, 6 Door/Window Sensors, 2 Motion Detectors, 1 Indoor HD Camera, Smart Home Integration, 24/7 Professional Monitoring, Cellular Backup, Free Installation + 1-Year Service Plan. Original price $1,299.',
          price: '649',
          priceCurrency: 'CAD',
          availability: 'https://schema.org/InStock',
          priceValidUntil: '2025-12-31',
          url: 'https://alliancesecuritysystems.com/alarm-promo',
        },
        {
          '@type': 'Offer',
          name: 'Premium Alarm Package — Promo Price',
          description: 'Smart Touchscreen Panel, 10 Door/Window Sensors, 3 Motion Detectors, 2 Indoor + 1 Outdoor HD Camera, Smart Lock Integration, Alexa/Google Home, 24/7 Monitoring, Cellular Backup, Free Installation + 2-Year Service Plan. Original price $1,999.',
          price: '999',
          priceCurrency: 'CAD',
          availability: 'https://schema.org/InStock',
          priceValidUntil: '2025-12-31',
          url: 'https://alliancesecuritysystems.com/alarm-promo',
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://alliancesecuritysystems.com/alarm-promo#service',
      name: 'Alarm System Installation — Promotional Offer',
      description: 'Professional wireless alarm system installation at 50% off for GTA homeowners. Includes free home assessment, 24/7 monitoring, battery and cellular backup, mobile app control, and factory-certified technicians.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      areaServed: { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '399',
        highPrice: '999',
        priceCurrency: 'CAD',
        offerCount: '3',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEO
        title="Limited-Time Alarm System Installation Offer | Alliance Security Systems GTA"
        description="Save 50% on professional alarm system installation — limited-time offer for GTA homeowners. Free assessment, same-day quotes, 24/7 monitoring. Call 1-888-458-9181."
        keywords="alarm system installation promo, security system deal GTA, home alarm discount Toronto, Alliance Security promo"
        canonical="/alarm-promo"
        structuredData={promoStructuredData}
      />

      {/* Sticky Top Bar */}
      <div className="bg-[#D4A853] text-white text-center py-2.5 px-4 text-sm font-semibold z-50 relative">
        <i className="ri-alarm-warning-line mr-2"></i>
        LIMITED TIME: 50% OFF Installation — Offer Ends In&nbsp;
        <span className="font-extrabold">{pad(countdown.days)}d {pad(countdown.hours)}h {pad(countdown.minutes)}m {pad(countdown.seconds)}s</span>
        <button onClick={scrollToForm} className="ml-4 bg-white text-[#D4A853] px-4 py-1 rounded-full text-xs font-bold hover:bg-[#0A1628] hover:text-white transition-all cursor-pointer whitespace-nowrap">
          Claim Now
        </button>
      </div>

      {/* Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-lg font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
              <span className="text-[8px] font-semibold tracking-[0.2em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Home</Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Alarm Packages</Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Contact</Link>
            <a href="tel:18884589181" className="text-sm font-medium text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
              <i className="ri-phone-line"></i> 1-888-458-9181
            </a>
            <button onClick={scrollToForm} className="bg-[#D4A853] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-md">
              Claim 50% Off
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-2.5 text-sm font-medium text-gray-700 border-b border-gray-50">Home</Link>
            <Link to="/alarm-packages" className="block py-2.5 text-sm font-medium text-gray-700 border-b border-gray-50">Alarm Packages</Link>
            <Link to="/contact" className="block py-2.5 text-sm font-medium text-gray-700 border-b border-gray-50">Contact</Link>
            <a href="tel:18884589181" className="block py-2.5 text-sm font-medium text-gray-700 border-b border-gray-50">1-888-458-9181</a>
            <button onClick={scrollToForm} className="w-full bg-[#D4A853] text-white py-3 rounded-full text-sm font-semibold mt-2 cursor-pointer whitespace-nowrap">Claim 50% Off</button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/15f70371b2616d28d0a9a75765bc5385.jpg"
            alt="Secure Home"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/30"></div>
        </div>

        {/* Floating urgency badge */}
        <div
          className="absolute top-8 right-8 hidden lg:flex flex-col items-center bg-[#D4A853] text-white rounded-2xl px-6 py-5 shadow-2xl"
          style={{ opacity: heroLoaded ? 1 : 0, transition: 'opacity 1s ease 0.8s' }}
        >
          <span className="text-xs font-bold tracking-widest mb-1 opacity-80">OFFER EXPIRES IN</span>
          <div className="flex items-center gap-2">
            {[{ v: countdown.days, l: 'Days' }, { v: countdown.hours, l: 'Hrs' }, { v: countdown.minutes, l: 'Min' }, { v: countdown.seconds, l: 'Sec' }].map(({ v, l }) => (
              <div key={l} className="flex flex-col items-center bg-white/20 rounded-xl px-3 py-2 min-w-[48px]">
                <span className="text-2xl font-extrabold leading-none">{pad(v)}</span>
                <span className="text-[10px] opacity-70 mt-0.5">{l}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-16">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-bold mb-6"
              style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s' }}
            >
              <i className="ri-fire-line"></i> LIMITED-TIME PROMOTIONAL OFFER
            </div>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-4"
              style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s' }}
            >
              50% Off Alarm System Installation — This Week Only
            </h1>
            <p
              className="text-lg sm:text-xl text-white/85 mb-4 leading-relaxed"
              style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(25px)', transition: 'opacity 0.7s ease 0.35s, transform 0.7s ease 0.35s' }}
            >
              Protect your home with a professionally installed alarm system at half the price. Trusted by 30,000+ GTA homeowners since 1999.
            </p>
            <ul
              className="space-y-2 mb-8"
              style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 0.45s, transform 0.7s ease 0.45s' }}
            >
              {['Free professional installation (save up to $500)', 'Free home security assessment', '24/7 professional monitoring included', 'No long-term contracts required'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-white/90 text-sm sm:text-base">
                  <span className="w-5 h-5 flex items-center justify-center bg-[#D4A853] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-xs"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <div
              className="flex flex-col sm:flex-row gap-3"
              style={{ opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 0.55s, transform 0.7s ease 0.55s' }}
            >
              <button
                onClick={scrollToForm}
                className="bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-bold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl flex items-center justify-center gap-2"
              >
                <i className="ri-gift-line text-lg"></i>
                Claim My 50% Off Now
              </button>
              <a
                href="tel:18884589181"
                className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call 1-888-458-9181
              </a>
            </div>
            <p
              className="mt-4 text-white/50 text-xs"
              style={{ opacity: heroLoaded ? 1 : 0, transition: 'opacity 0.7s ease 0.7s' }}
            >
              * Offer valid for new residential installations in the Greater Toronto Area. Limited spots available.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#0A1628] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: 'ri-shield-star-line', text: '25+ Years Experience' },
              { icon: 'ri-home-4-line', text: '30,000+ Installations' },
              { icon: 'ri-award-line', text: 'BBB A+ Rated' },
              { icon: 'ri-time-line', text: '24/7 Monitoring' },
              { icon: 'ri-tools-line', text: 'Factory-Certified Techs' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-white/80 text-sm font-medium whitespace-nowrap">
                <i className={`${icon} text-[#D4A853] text-lg`}></i>
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 sm:py-24 bg-white">
        <div
          ref={benefitsAnim.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6"
          style={{ opacity: benefitsAnim.visible ? 1 : 0, transform: benefitsAnim.visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="text-center mb-12">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-3 whitespace-nowrap">WHAT'S INCLUDED</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-3">Everything You Need to Stay Safe</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">Every promo package includes professional installation, monitoring, and full support — no hidden fees.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-install-line', title: 'Free Professional Installation', desc: 'Our certified technicians handle everything. No DIY headaches — we install, test, and train you on your new system.', value: 'Value: $500' },
              { icon: 'ri-search-eye-line', title: 'Free Home Security Assessment', desc: 'We evaluate your property\'s vulnerabilities and recommend the ideal coverage before any commitment.', value: 'Value: $150' },
              { icon: 'ri-smartphone-line', title: 'Mobile App Control', desc: 'Arm/disarm, receive alerts, and view live camera feeds from anywhere using our intuitive mobile app.', value: 'Included' },
              { icon: 'ri-live-line', title: '24/7 Professional Monitoring', desc: 'Our monitoring centre watches over your home around the clock and dispatches emergency services instantly.', value: 'From $29/mo' },
              { icon: 'ri-battery-charge-line', title: 'Battery & Cellular Backup', desc: 'Your system stays active even during power outages or internet disruptions — always protected.', value: 'Included' },
              { icon: 'ri-customer-service-2-line', title: 'Lifetime Technical Support', desc: 'We don\'t disappear after installation. Our local team is always available for questions, repairs, and upgrades.', value: 'Included' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#F8F9FA] rounded-2xl p-6 border border-gray-100 hover:border-[#D4A853]/40 hover:shadow-lg transition-all duration-300 group"
                style={{ opacity: benefitsAnim.visible ? 1 : 0, transform: benefitsAnim.visible ? 'translateY(0)' : 'translateY(30px)', transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s` }}
              >
                <div className="w-12 h-12 bg-[#D4A853] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-3">{item.desc}</p>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] text-xs font-bold px-3 py-1 rounded-full">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 sm:py-24 bg-[#F8F9FA]">
        <div
          ref={packagesAnim.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6"
          style={{ opacity: packagesAnim.visible ? 1 : 0, transform: packagesAnim.visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="text-center mb-12">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-3 whitespace-nowrap">PROMO PACKAGES</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-3">Choose Your Protection Level</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">All packages include 50% off installation this week only. Prices shown are promotional rates.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {packages.map((pkg, i) => (
              <div
                key={i}
                className={`relative rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 ${pkg.highlight ? 'bg-[#0A1628] text-white shadow-2xl ring-2 ring-[#D4A853] scale-[1.02]' : 'bg-white text-[#0A1628] shadow-lg border border-gray-100'}`}
                style={{ opacity: packagesAnim.visible ? 1 : 0, transform: packagesAnim.visible ? (pkg.highlight ? 'scale(1.02)' : 'translateY(0)') : 'translateY(30px)', transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s` }}
              >
                {pkg.highlight && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A853] via-[#f0c97a] to-[#D4A853]"></div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-xl font-extrabold ${pkg.highlight ? 'text-white' : 'text-[#0A1628]'}`}>{pkg.name}</h3>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${pkg.badgeColor}`}>{pkg.badge}</span>
                  </div>
                  <div className="mb-5">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className={`text-sm line-through ${pkg.highlight ? 'text-white/40' : 'text-gray-400'}`}>{pkg.originalPrice}</span>
                      <span className="text-xs font-semibold text-[#D4A853]">50% OFF</span>
                    </div>
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-extrabold text-[#D4A853]">{pkg.promoPrice}</span>
                      <span className={`text-sm mb-1 ${pkg.highlight ? 'text-white/60' : 'text-gray-400'}`}>one-time</span>
                    </div>
                    <p className={`text-sm mt-1 ${pkg.highlight ? 'text-white/70' : 'text-gray-500'}`}>+ {pkg.monthly}/mo monitoring</p>
                  </div>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <span className="w-4 h-4 flex items-center justify-center bg-[#D4A853] rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                        <span className={pkg.highlight ? 'text-white/85' : 'text-gray-600'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={scrollToForm}
                    className={`w-full py-3.5 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${pkg.highlight ? 'bg-[#D4A853] text-white hover:bg-[#B8923D]' : 'bg-[#0A1628] text-white hover:bg-[#D4A853]'}`}
                  >
                    Claim This Package
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-xs mt-6">* Custom packages available. Contact us for commercial or multi-unit properties.</p>
        </div>
      </section>

      {/* Urgency Banner */}
      <section
        ref={urgencyAnim.ref}
        className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D] text-white"
        style={{ opacity: urgencyAnim.visible ? 1 : 0, transform: urgencyAnim.visible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm font-bold tracking-widest mb-2 opacity-80">DON'T MISS OUT — OFFER ENDS IN</p>
          <div className="flex items-center justify-center gap-3 sm:gap-5 mb-6">
            {[{ v: countdown.days, l: 'Days' }, { v: countdown.hours, l: 'Hours' }, { v: countdown.minutes, l: 'Minutes' }, { v: countdown.seconds, l: 'Seconds' }].map(({ v, l }) => (
              <div key={l} className="flex flex-col items-center bg-white/20 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 min-w-[64px] sm:min-w-[80px]">
                <span className="text-3xl sm:text-4xl font-extrabold leading-none">{pad(v)}</span>
                <span className="text-xs opacity-70 mt-1">{l}</span>
              </div>
            ))}
          </div>
          <p className="text-white/90 mb-5 text-base sm:text-lg font-medium">Only a limited number of promo slots remain. Secure yours before they're gone.</p>
          <button
            onClick={scrollToForm}
            className="bg-white text-[#D4A853] px-10 py-4 rounded-full font-extrabold text-base hover:bg-[#0A1628] hover:text-white transition-all cursor-pointer whitespace-nowrap shadow-xl"
          >
            Claim My 50% Off Now
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 bg-white">
        <div
          ref={stepsAnim.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6"
          style={{ opacity: stepsAnim.visible ? 1 : 0, transform: stepsAnim.visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="text-center mb-12">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-sm font-semibold mb-3 whitespace-nowrap">HOW IT WORKS</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-3">Protected in 4 Simple Steps</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">From first contact to full protection — we make it effortless.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div
                key={i}
                className="relative flex flex-col items-center text-center"
                style={{ opacity: stepsAnim.visible ? 1 : 0, transform: stepsAnim.visible ? 'translateY(0)' : 'translateY(30px)', transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s` }}
              >
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+32px)] right-0 h-0.5 bg-[#D4A853]/30 z-0"></div>
                )}
                <div className="relative z-10 w-16 h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <i className={`${step.icon} text-2xl text-white`}></i>
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-[#0A1628] text-white text-xs font-extrabold rounded-full flex items-center justify-center">{i + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24 bg-[#0A1628]">
        <div
          ref={testimonialsAnim.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6"
          style={{ opacity: testimonialsAnim.visible ? 1 : 0, transform: testimonialsAnim.visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="text-center mb-12">
            <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-3 whitespace-nowrap">HAPPY CUSTOMERS</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">GTA Homeowners Love Us</h2>
            <p className="text-white/60 max-w-xl mx-auto text-base">Real reviews from real customers who took advantage of our promo.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-all"
                style={{ opacity: testimonialsAnim.visible ? 1 : 0, transform: testimonialsAnim.visible ? 'translateY(0)' : 'translateY(30px)', transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s` }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.stars)].map((_, s) => <i key={s} className="ri-star-fill text-[#D4A853]"></i>)}
                </div>
                <i className="ri-double-quotes-l text-4xl text-[#D4A853]/40 mb-2 block"></i>
                <p className="text-white/80 text-sm leading-relaxed mb-5">"{t.text}"</p>
                <div>
                  <p className="text-white font-bold text-sm">{t.name}</p>
                  <p className="text-white/40 text-xs flex items-center gap-1 mt-0.5">
                    <i className="ri-map-pin-line text-[#D4A853]"></i>{t.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section id="claim-offer" className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20home%20interior%20living%20room%20with%20warm%20ambient%20lighting%20cozy%20safe%20family%20home%20blurred%20background%20soft%20bokeh%20comfortable%20residential%20interior%20design%20neutral%20tones&width=1920&height=1080&seq=alarmpromo-form1&orientation=landscape"
            alt="Secure Home Interior"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/85 to-[#0A1628]/60"></div>
        </div>
        <div
          ref={formAnim.ref}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6"
          style={{ opacity: formAnim.visible ? 1 : 0, transform: formAnim.visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left copy */}
            <div>
              <span className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-bold mb-5">
                <i className="ri-fire-line"></i> CLAIM YOUR PROMO NOW
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
                Get 50% Off Installation — Fill Out the Form
              </h2>
              <p className="text-white/70 text-base mb-6 leading-relaxed">
                Submit your details and one of our security experts will contact you within 2 hours to confirm your promo slot and schedule your free assessment.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-time-line', text: 'Response within 2 hours' },
                  { icon: 'ri-calendar-check-line', text: 'Same-week installation available' },
                  { icon: 'ri-lock-line', text: 'No obligation — 100% free assessment' },
                  { icon: 'ri-shield-check-line', text: 'Licensed & insured technicians' },
                ].map(({ icon, text }) => (
                  <div key={text} className="flex items-center gap-3 text-white/80 text-sm">
                    <span className="w-8 h-8 flex items-center justify-center bg-[#D4A853]/20 rounded-full flex-shrink-0">
                      <i className={`${icon} text-[#D4A853]`}></i>
                    </span>
                    {text}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-2xl">
                <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs mb-0.5">Prefer to call directly?</p>
                  <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">1-888-458-9181</a>
                </div>
              </div>
            </div>

            {/* Right form */}
            <div className="bg-white rounded-2xl p-7 sm:p-9 shadow-2xl">
              {formSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">You're on the list!</h3>
                  <p className="text-gray-500 text-sm mb-4">We'll contact you within 2 hours to confirm your promo slot and schedule your free assessment.</p>
                  <a href="tel:18884589181" className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-phone-line"></i> Call Us Now
                  </a>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h3 className="text-xl font-extrabold text-[#0A1628] mb-1">Claim Your 50% Off</h3>
                    <p className="text-gray-400 text-sm">Fill in your details — takes less than 60 seconds.</p>
                  </div>
                  <form data-readdy-form id="alarm-promo-form" onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">First Name</label>
                        <input type="text" name="first_name" required placeholder="John" className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Last Name</label>
                        <input type="text" name="last_name" required placeholder="Doe" className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                      <input type="email" name="email" required placeholder="john@example.com" className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                      <input type="tel" name="phone" required placeholder="(905) 555-0100" className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Property Type</label>
                        <select name="property_type" required className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] cursor-pointer">
                          <option value="">Select...</option>
                          <option value="house">House</option>
                          <option value="condo">Condo</option>
                          <option value="townhouse">Townhouse</option>
                          <option value="commercial">Commercial</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">City</label>
                        <input type="text" name="city" required placeholder="Mississauga" className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Anything else? <span className="text-gray-400 font-normal">(optional)</span></label>
                      <textarea name="message" rows={3} maxLength={500} placeholder="e.g. I have a 3-bedroom home and want cameras too..." className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] resize-none"></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#D4A853] text-white py-4 rounded-full font-extrabold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {submitting ? <><i className="ri-loader-4-line animate-spin"></i> Submitting...</> : <><i className="ri-gift-line"></i> Claim My 50% Off Now</>}
                    </button>
                    <p className="text-center text-gray-400 text-xs">No spam. No obligation. We respect your privacy.</p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
            <span className="text-base font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
            <span className="text-[8px] font-semibold tracking-[0.2em] text-[#D4A853]">SECURITY SYSTEMS</span>
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/50">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <Link to="/alarm-packages" className="hover:text-white transition-colors cursor-pointer">Alarm Packages</Link>
            <Link to="/contact" className="hover:text-white transition-colors cursor-pointer">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</Link>
          </div>
          <a href="tel:18884589181" className="flex items-center gap-2 text-[#D4A853] font-semibold text-sm whitespace-nowrap hover:text-white transition-colors cursor-pointer">
            <i className="ri-phone-line"></i> 1-888-458-9181
          </a>
        </div>
      </footer>
    </div>
  );
}
